import { RefObject, useEffect, useRef, useState } from 'react';

/**
 * Returns a ref and a boolean indicating whether the referenced element is currently in view.
 *
 * @param rootMargin - CSS margin around the root used to grow or shrink the intersection area.
 *   Follows the CSS `margin` shorthand syntax (top right bottom left).
 *
 *   Common values:
 *   - `'-50% 0% -50% 0%'` (default) — fires when the element's center crosses the viewport center.
 *     Best for scroll-triggered animations.
 *   - `'0px'` — fires as soon as any part of the element enters the viewport.
 *   - `'100px 0px'` — fires 100px *before* the element reaches the viewport edge.
 *     Best for preloading heavy content (maps, videos) before the user scrolls to it.
 *   - `'-100px 0px'` — fires when the element is already 100px inside the viewport.
 *     Best for ensuring the element is well-visible before triggering.
 *
 * @example
 * // Scroll animation: trigger when section center hits viewport center
 * const [ref, isInView] = useSectionIsInView();
 *
 * @example
 * // Preload: trigger 100px before section enters viewport
 * const [ref, isInView] = useSectionIsInView('100px 0px');
 */
export const useSectionIsInView = <T extends HTMLElement = HTMLDivElement>(
  rootMargin = '-50% 0% -50% 0%',
): [RefObject<T>, boolean] => {
  const sectionRef = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!sectionRef.current) return;

    const observer = new IntersectionObserver(([entry]) => setInView(!!entry?.isIntersecting), {
      rootMargin,
      threshold: 0,
    });

    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [rootMargin]);

  return [sectionRef, inView];
};
